'use client';

import { Crown, Diamond, Medal, Shield } from '@phosphor-icons/react';
import type { LoyaltyLevel, LoyaltyTierInfo } from '@/lib/api/loyalty';

const TIER_ICONS = [Shield, Medal, Diamond, Crown];
const TIER_COLORS = [
  { bg: 'bg-[#C9956B]', ring: 'ring-[#C9956B]/30', text: 'text-[#A67648]' },
  { bg: 'bg-[#A8B8C8]', ring: 'ring-[#A8B8C8]/30', text: 'text-[#7D8FA0]' },
  { bg: 'bg-[#D4A853]', ring: 'ring-[#D4A853]/30', text: 'text-[#B8912A]' },
  { bg: 'bg-[#7B42FF]', ring: 'ring-[#7B42FF]/30', text: 'text-[#7B42FF]' },
];

interface LevelsStepperProps {
  currentLevel: number;
  allLevels: LoyaltyTierInfo[];
  level?: LoyaltyLevel;
}

export function LevelsStepper({ currentLevel, allLevels, level }: LevelsStepperProps) {
  return (
    <div className="flex items-start justify-between relative">
      {/* Connecting line */}
      <div className="absolute top-5 left-[calc(12.5%)] right-[calc(12.5%)] h-[2px] bg-[var(--border-default)]" />
      <div
        className="absolute top-5 left-[calc(12.5%)] h-[2px] bg-[var(--primary)] transition-all duration-500"
        style={{
          width: currentLevel >= 4
            ? '100%'
            : `${((currentLevel - 1) / 3) * 100}%`,
          maxWidth: currentLevel >= 4 ? 'calc(100% - 25%)' : undefined,
        }}
      />

      {allLevels.map((tier, i) => {
        const Icon = TIER_ICONS[i];
        const colors = TIER_COLORS[i];
        const isReached = currentLevel >= tier.level;
        const isCurrent = currentLevel === tier.level;

        return (
          <div key={tier.level} className="flex flex-col items-center relative z-10" style={{ width: '25%' }}>
            {/* Circle */}
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
              ${isReached
                ? `${colors.bg} ring-4 ${colors.ring} ${isCurrent ? 'scale-110 shadow-lg' : ''}`
                : 'bg-[var(--surface-secondary)] border-2 border-dashed border-[var(--border-default)]'
              }
            `}>
              <Icon
                className={`w-4 h-4 ${isReached ? 'text-white' : 'text-[var(--text-tertiary)]'}`}
                weight={isReached ? 'fill' : 'regular'}
              />
            </div>

            {/* Label */}
            <p className={`text-xs font-bold mt-2 ${isCurrent ? colors.text : isReached ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>
              {tier.name}
            </p>
            <p className={`text-sm font-extrabold font-heading ${isCurrent ? colors.text : isReached ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}>
              {tier.earn_rate}%
            </p>
            <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
              {tier.min_total === 0 ? 'Сразу' : `от ${(tier.min_total / 1000).toFixed(0)}т ₽`}
            </p>
          </div>
        );
      })}
    </div>
  );
}
